import { useEffect, useMemo, useState } from "react";
import {
  archiveThread,
  blockUser,
  getThreads,
  markAllThreadsAsDelivered,
  unblockUser,
} from "../service";
import { Thread } from "../types";
import { useDispatch, useSelector } from "react-redux";
import {
  refreshThreadsDetails,
  refreshThreadsList,
  selectSelectedThreadId,
  selectThreadsListVersion,
  setSelectedThreadId,
} from "../messagingThreadSlice";
import { useNavigate } from "react-router-dom";

export const useThreadsList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedThreadId = useSelector(selectSelectedThreadId);
  const refreshThreadList = useSelector(selectThreadsListVersion);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [blockTarget, setBlockTarget] = useState<Thread | null>(null);
  const [blocking, setBlocking] = useState(false);

  const fetchThreads = async () => {
    try {
      const data = await getThreads();
      setThreads(data);

      const threadIds = data.map((thread) => thread.id);

      if (threadIds.length > 0) {
        await markAllThreadsAsDelivered(threadIds);
      }
    } catch (error) {
      console.error("Failed to fetch threads", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchThreads();
  }, []);

  useEffect(() => {
    fetchThreads();
  }, [refreshThreadList]);

  const filteredThreads = useMemo(() => {
    return threads
      .filter((t) => (showArchived ? t.isArchived : !t.isArchived))
      .filter((t) =>
        `${t.participant.firstName} ${t.participant.lastName}`
          .toLowerCase()
          .includes(search.toLowerCase())
      )
      .sort((a, b) => Number(b.isPinned) - Number(a.isPinned));
  }, [threads, search, showArchived]);

  const handleSelectedThread = (threadId: string) => {
    dispatch(setSelectedThreadId(threadId));
    navigate(`/threads/${threadId}`);
  };

  const handleArchiveThread = async (threadId: string, isArchived: boolean) => {
    try {
      await archiveThread(threadId, !isArchived);
      dispatch(refreshThreadsList());
      dispatch(refreshThreadsDetails());
    } catch (err) {
      console.error("Failed to archive thread", err);
    }
  };

  const confirmBlockToggle = async () => {
    if (!blockTarget) return;

    setBlocking(true);
    try {
      const userId = blockTarget.participant.id;

      if (blockTarget.blockContext.blockedByMe) {
        await unblockUser(userId);
      } else {
        await blockUser(userId);
      }
      dispatch(refreshThreadsList());
      dispatch(refreshThreadsDetails());
    } finally {
      setBlocking(false);
      setBlockTarget(null);
    }
  };

  return {
    threads: filteredThreads,
    loading,
    search,
    setSearch,
    showArchived,
    setShowArchived,
    handleSelectedThread,
    selectedThreadId,
    handleArchiveThread,
    blockTarget,
    setBlockTarget,
    confirmBlockToggle,
    blocking,
  };
};
